// filepath: frontend/src/pages/EventsPage.js
import React, { useState, useEffect } from 'react';
import useAuth from '../hooks/useAuth';
import {
    Box,
    Paper,
    Typography,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    IconButton,
    Chip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    MenuItem,
    FormControlLabel,
    Checkbox,
    Grid,
    Card,
    CardContent,
    CardActions,
    Tabs,
    Tab,
    Toolbar,
    useTheme,
    useMediaQuery,
    Divider,
    Avatar
} from '@mui/material';
import {
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    People as PeopleIcon,
    EmojiEvents as EventIcon,
    CalendarToday as CalendarIcon,
    AccessTime,
    LocationOn
} from '@mui/icons-material';
import useAxiosPrivate from '../hooks/useAxiosPrivate';
import { useTranslation } from 'react-i18next';

const EventsPage = () => {
    const { auth } = useAuth();
    const axiosPrivate = useAxiosPrivate();
    const { t } = useTranslation('global');
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    
    const [events, setEvents] = useState([]);
    const [elderly, setElderly] = useState([]);
    const [openDialog, setOpenDialog] = useState(false);
    const [openParticipantsDialog, setOpenParticipantsDialog] = useState(false);
    const [selectedEvent, setSelectedEvent] = useState(null);
    const [viewMode, setViewMode] = useState(0); // 0: Lista, 1: Calendario, 2: Cards
    const [formData, setFormData] = useState({
        title: '',
        description: '',
        event_date: '',
        start_time: '',
        end_time: '',
        event_type: 'altro',
        location: '',
        max_participants: '',
        organizer_name: '',
        organizer_phone: '',
        cost: 0,
        materials_needed: '',
        notes: '',
        status: 'scheduled',
        is_public: true,
        requires_registration: false,
        is_recurring: false,
        recurrence_pattern: '',
        recurrence_end_date: ''
    });

    const eventTypes = [
        { value: 'festa', label: 'Festa', color: '#e91e63' },
        { value: 'giochi', label: 'Giochi', color: '#9c27b0' },
        { value: 'ginnastica', label: 'Ginnastica', color: '#4caf50' },
        { value: 'musicoterapia', label: 'Musicoterapia', color: '#2196f3' },
        { value: 'laboratorio', label: 'Laboratorio', color: '#ff9800' },
        { value: 'gita', label: 'Gita', color: '#00bcd4' },
        { value: 'conferenza', label: 'Conferenza', color: '#607d8b' },
        { value: 'compleanno', label: 'Compleanno', color: '#f44336' },
        { value: 'religioso', label: 'Religioso', color: '#795548' },
        { value: 'altro', label: 'Altro', color: '#9e9e9e' }
    ];

    const statusTypes = [
        { value: 'scheduled', label: 'Programmato' },
        { value: 'in_progress', label: 'In Corso' },
        { value: 'completed', label: 'Completato' },
        { value: 'cancelled', label: 'Annullato' }
    ];

    useEffect(() => {
        if (!auth?.accessToken) {
            return;
        }
        loadEvents();
        loadElderly();
    }, [auth?.accessToken]);

    const loadEvents = async () => {
        try {
            const response = await axiosPrivate.get('/events');
            setEvents(response.data);
        } catch (error) {
            console.error('Errore nel caricamento eventi:', error);
        }
    };

    const loadElderly = async () => {
        try {
            const response = await axiosPrivate.get('/elderly');
            setElderly(response.data);
        } catch (error) {
            console.error('Errore nel caricamento anziani:', error);
        }
    };

    const handleOpenDialog = (event = null) => {
        if (event) {
            setFormData({
                ...event,
                event_date: event.event_date.split('T')[0],
                is_recurring: event.is_recurring === 1,
                is_public: event.is_public === 1,
                requires_registration: event.requires_registration === 1
            });
            setSelectedEvent(event);
        } else {
            setFormData({
                title: '',
                description: '',
                event_date: '',
                start_time: '',
                end_time: '',
                event_type: 'altro',
                location: '',
                max_participants: '',
                organizer_name: '',
                organizer_phone: '',
                cost: 0,
                materials_needed: '',
                notes: '',
                status: 'scheduled',
                is_public: true,
                requires_registration: false,
                is_recurring: false,
                recurrence_pattern: '',
                recurrence_end_date: ''
            });
            setSelectedEvent(null);
        }
        setOpenDialog(true);
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
        setSelectedEvent(null);
    };

    const handleSubmit = async () => {
        try {
            if (selectedEvent) {
                await axiosPrivate.patch(`/events/${selectedEvent.event_id}`, formData);
            } else {
                await axiosPrivate.post('/events', formData);
            }
            loadEvents();
            handleCloseDialog();
        } catch (error) {
            console.error('Errore nel salvare evento:', error);
        }
    };

    const handleDelete = async (eventId) => {
        if (window.confirm('Sei sicuro di voler eliminare questo evento?')) {
            try {
                await axiosPrivate.delete(`/events/${eventId}`);
                loadEvents();
            } catch (error) {
                console.error('Errore nell\'eliminazione evento:', error);
            }
        }
    };

    const handleOpenParticipants = async (event) => {
        try {
            const response = await axiosPrivate.get(`/events/${event.event_id}`);
            setSelectedEvent(response.data);
            setOpenParticipantsDialog(true);
        } catch (error) {
            console.error('Errore nel caricamento partecipanti:', error);
        }
    };

    const handleRegisterParticipant = async (elderlyId) => {
        try {
            await axiosPrivate.post('/events/participants', {
                event_id: selectedEvent.event_id,
                elderly_id: elderlyId
            });
            handleOpenParticipants(selectedEvent);
            loadEvents();
        } catch (error) {
            console.error('Errore nell\'iscrizione:', error);
        }
    };

    const handleUnregisterParticipant = async (elderlyId) => {
        try {
            await axiosPrivate.delete(`/events/participants/${selectedEvent.event_id}/${elderlyId}`);
            handleOpenParticipants(selectedEvent);
            loadEvents();
        } catch (error) {
            console.error('Errore nella rimozione iscrizione:', error);
        }
    };

    const getTypeColor = (type) => {
        const eventType = eventTypes.find(t => t.value === type);
        return eventType ? eventType.color : '#9e9e9e';
    };

    const getTypeLabel = (type) => {
        const eventType = eventTypes.find(t => t.value === type);
        return eventType ? eventType.label : type;
    };

    const getStatusLabel = (status) => {
        const statusType = statusTypes.find(s => s.value === status);
        return statusType ? statusType.label : status;
    };

    const formatDate = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        return date.toLocaleDateString('it-IT', { 
            day: '2-digit', 
            month: 'long', 
            year: 'numeric' 
        });
    };

    const formatTime = (timeString) => {
        if (!timeString) return '';
        return timeString.substring(0, 5);
    };

    // Componente Card per visualizzazione mobile
    const EventCardMobile = ({ event }) => (
        <Card sx={{ mb: 2 }}>
            <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                    <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                        {event.title}
                    </Typography>
                    <Chip
                        label={getStatusLabel(event.status)}
                        size="small"
                        color={event.status === 'completed' ? 'success' : 
                               event.status === 'cancelled' ? 'error' : 'default'}
                    />
                </Box>

                <Chip
                    label={getTypeLabel(event.event_type)}
                    size="small"
                    sx={{ bgcolor: getTypeColor(event.event_type), color: 'white', mb: 1.5 }}
                />

                <Divider sx={{ my: 1.5 }} />

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <CalendarIcon fontSize="small" color="action" />
                    <Typography variant="body2">{formatDate(event.event_date)}</Typography>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <AccessTime fontSize="small" color="action" />
                    <Typography variant="body2">
                        {formatTime(event.start_time)} - {formatTime(event.end_time)}
                    </Typography>
                </Box>

                {event.location && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <LocationOn fontSize="small" color="action" />
                        <Typography variant="body2">{event.location}</Typography>
                    </Box>
                )}

                {event.description && (
                    <Box sx={{ p: 1, bgcolor: 'action.hover', borderRadius: 1, my: 1.5 }}>
                        <Typography variant="body2">{event.description}</Typography>
                    </Box>
                )}

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <PeopleIcon fontSize="small" color="action" />
                    <Typography variant="body2">
                        Partecipanti: {event.registered_count || 0}
                        {event.max_participants && ` / ${event.max_participants}`}
                    </Typography>
                </Box>

                <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                        variant="outlined"
                        size="small"
                        startIcon={<PeopleIcon />}
                        onClick={() => handleOpenParticipants(event)}
                        fullWidth
                    >
                        Partecipanti
                    </Button>
                    <Button
                        variant="outlined"
                        size="small"
                        startIcon={<EditIcon />}
                        onClick={() => handleOpenDialog(event)}
                        fullWidth
                    >
                        Modifica
                    </Button>
                    <IconButton
                        color="error"
                        size="small"
                        onClick={() => handleDelete(event.event_id)}
                    >
                        <DeleteIcon />
                    </IconButton>
                </Box>
            </CardContent>
        </Card>
    );

    return (
        <Box sx={{ p: { xs: 1, sm: 2, md: 3 } }}>
            <Toolbar />
            <Box sx={{ 
                display: 'flex', 
                flexDirection: { xs: 'column', sm: 'row' },
                justifyContent: 'space-between', 
                alignItems: { xs: 'stretch', sm: 'center' },
                gap: 2,
                mb: 3 
            }}>
                <Typography variant={isMobile ? "h5" : "h4"}>
                    <EventIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                    Gestione Eventi
                </Typography>
                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => handleOpenDialog()}
                    fullWidth={isMobile}
                >
                    Nuovo Evento
                </Button>
            </Box>

            {!isMobile && (
                <Tabs value={viewMode} onChange={(e, newValue) => setViewMode(newValue)} sx={{ mb: 2 }}>
                    <Tab label="Vista Lista" />
                    <Tab label="Vista Cards" />
                </Tabs>
            )}

            {isMobile ? (
                <Box>
                    {events.length === 0 ? (
                        <Typography color="text.secondary" align="center" sx={{ py: 4 }}>
                            Nessun evento presente
                        </Typography>
                    ) : (
                        events.map((event) => (
                            <EventCardMobile key={event.event_id} event={event} />
                        ))
                    )}
                </Box>
            ) : viewMode === 0 ? (
                <TableContainer component={Paper}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Titolo</TableCell>
                                <TableCell>Data</TableCell>
                                <TableCell>Orario</TableCell>
                                <TableCell>Tipo</TableCell>
                                <TableCell>Luogo</TableCell>
                                <TableCell>Partecipanti</TableCell>
                                <TableCell>Stato</TableCell>
                                <TableCell>Azioni</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {events.map((event) => (
                                <TableRow key={event.event_id}>
                                    <TableCell>
                                        <Typography variant="body1" fontWeight="bold">
                                            {event.title}
                                        </Typography>
                                        {event.description && (
                                            <Typography variant="caption" color="text.secondary">
                                                {event.description.substring(0, 50)}...
                                            </Typography>
                                        )}
                                    </TableCell>
                                    <TableCell>{formatDate(event.event_date)}</TableCell>
                                    <TableCell>
                                        {formatTime(event.start_time)} - {formatTime(event.end_time)}
                                    </TableCell>
                                    <TableCell>
                                        <Chip
                                            label={getTypeLabel(event.event_type)}
                                            size="small"
                                            sx={{ 
                                                bgcolor: getTypeColor(event.event_type),
                                                color: 'white'
                                            }}
                                        />
                                    </TableCell>
                                    <TableCell>{event.location || '-'}</TableCell>
                                    <TableCell>
                                        {event.registered_count || 0}
                                        {event.max_participants && ` / ${event.max_participants}`}
                                    </TableCell>
                                    <TableCell>
                                        <Chip
                                            label={getStatusLabel(event.status)}
                                            size="small"
                                            color={event.status === 'completed' ? 'success' : 
                                                   event.status === 'cancelled' ? 'error' : 'default'}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <IconButton
                                            size="small"
                                            onClick={() => handleOpenParticipants(event)}
                                            title="Gestisci Partecipanti"
                                        >
                                            <PeopleIcon />
                                        </IconButton>
                                        <IconButton
                                            size="small"
                                            onClick={() => handleOpenDialog(event)}
                                        >
                                            <EditIcon />
                                        </IconButton>
                                        <IconButton
                                            size="small"
                                            onClick={() => handleDelete(event.event_id)}
                                            color="error"
                                        >
                                            <DeleteIcon />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            ) : (
                <Grid container spacing={3}>
                    {events.map((event) => (
                        <Grid item xs={12} sm={6} md={4} key={event.event_id}>
                            <Card>
                                <CardContent>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                                        <Chip
                                            label={getTypeLabel(event.event_type)}
                                            size="small"
                                            sx={{ 
                                                bgcolor: getTypeColor(event.event_type),
                                                color: 'white'
                                            }}
                                        />
                                        <Chip
                                            label={getStatusLabel(event.status)}
                                            size="small"
                                            color={event.status === 'completed' ? 'success' : 
                                                   event.status === 'cancelled' ? 'error' : 'default'}
                                        />
                                    </Box>
                                    <Typography variant="h6" gutterBottom>
                                        {event.title}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary" gutterBottom>
                                        {event.description?.substring(0, 100)}
                                        {event.description?.length > 100 && '...'}
                                    </Typography>
                                    <Box sx={{ mt: 2 }}>
                                        <Typography variant="body2">
                                            <CalendarIcon sx={{ fontSize: 16, verticalAlign: 'middle', mr: 0.5 }} />
                                            {formatDate(event.event_date)}
                                        </Typography>
                                        <Typography variant="body2">
                                            🕐 {formatTime(event.start_time)} - {formatTime(event.end_time)}
                                        </Typography>
                                        {event.location && (
                                            <Typography variant="body2">
                                                📍 {event.location}
                                            </Typography>
                                        )}
                                        <Typography variant="body2">
                                            <PeopleIcon sx={{ fontSize: 16, verticalAlign: 'middle', mr: 0.5 }} />
                                            {event.registered_count || 0}
                                            {event.max_participants && ` / ${event.max_participants}`} partecipanti
                                        </Typography>
                                    </Box>
                                </CardContent>
                                <CardActions>
                                    <Button size="small" onClick={() => handleOpenParticipants(event)}>
                                        Partecipanti
                                    </Button>
                                    <Button size="small" onClick={() => handleOpenDialog(event)}>
                                        Modifica
                                    </Button>
                                    <Button size="small" color="error" onClick={() => handleDelete(event.event_id)}>
                                        Elimina
                                    </Button>
                                </CardActions>
                            </Card>
                        </Grid>
                    ))}
                </Grid>
            )}

            {/* Dialog Evento */}
            <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
                <DialogTitle>
                    {selectedEvent ? 'Modifica Evento' : 'Nuovo Evento'}
                </DialogTitle>
                <DialogContent>
                    <Grid container spacing={2} sx={{ mt: 1 }}>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Titolo *"
                                value={formData.title}
                                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                multiline
                                rows={3}
                                label="Descrizione"
                                value={formData.description}
                                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                            />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <TextField
                                fullWidth
                                type="date"
                                label="Data *"
                                value={formData.event_date}
                                onChange={(e) => setFormData({ ...formData, event_date: e.target.value })}
                                InputLabelProps={{ shrink: true }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <TextField
                                fullWidth
                                type="time"
                                label="Ora Inizio *"
                                value={formData.start_time}
                                onChange={(e) => setFormData({ ...formData, start_time: e.target.value })}
                                InputLabelProps={{ shrink: true }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <TextField
                                fullWidth
                                type="time"
                                label="Ora Fine *"
                                value={formData.end_time}
                                onChange={(e) => setFormData({ ...formData, end_time: e.target.value })}
                                InputLabelProps={{ shrink: true }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                select
                                label="Tipo Evento *"
                                value={formData.event_type}
                                onChange={(e) => setFormData({ ...formData, event_type: e.target.value })}
                            >
                                {eventTypes.map((type) => (
                                    <MenuItem key={type.value} value={type.value}>
                                        {type.label}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                select
                                label="Stato"
                                value={formData.status}
                                onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                            >
                                {statusTypes.map((status) => (
                                    <MenuItem key={status.value} value={status.value}>
                                        {status.label}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Luogo"
                                value={formData.location}
                                onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                type="number"
                                label="Max Partecipanti"
                                value={formData.max_participants}
                                onChange={(e) => setFormData({ ...formData, max_participants: e.target.value })}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Organizzatore"
                                value={formData.organizer_name}
                                onChange={(e) => setFormData({ ...formData, organizer_name: e.target.value })}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Telefono Organizzatore"
                                value={formData.organizer_phone}
                                onChange={(e) => setFormData({ ...formData, organizer_phone: e.target.value })}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                multiline
                                rows={2}
                                label="Materiali Necessari"
                                value={formData.materials_needed}
                                onChange={(e) => setFormData({ ...formData, materials_needed: e.target.value })}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                multiline
                                rows={2}
                                label="Note"
                                value={formData.notes}
                                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                            />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={formData.is_public}
                                        onChange={(e) => setFormData({ ...formData, is_public: e.target.checked })}
                                    />
                                }
                                label="Pubblico"
                            />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={formData.requires_registration}
                                        onChange={(e) => setFormData({ ...formData, requires_registration: e.target.checked })}
                                    />
                                }
                                label="Richiede Iscrizione"
                            />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDialog}>Annulla</Button>
                    <Button 
                        onClick={handleSubmit} 
                        variant="contained"
                        disabled={!formData.title || !formData.event_date || !formData.start_time || !formData.end_time}
                    >
                        Salva
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Dialog Partecipanti */}
            <Dialog open={openParticipantsDialog} onClose={() => setOpenParticipantsDialog(false)} maxWidth="md" fullWidth>
                <DialogTitle>
                    Partecipanti - {selectedEvent?.title}
                </DialogTitle>
                <DialogContent>
                    {selectedEvent && (
                        <>
                            <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                                Iscritti ({selectedEvent.participants?.length || 0}
                                {selectedEvent.max_participants && ` / ${selectedEvent.max_participants}`})
                            </Typography>
                            <TableContainer>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Nome</TableCell>
                                            <TableCell>Camera</TableCell>
                                            <TableCell>Stato</TableCell>
                                            <TableCell>Azioni</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {selectedEvent.participants?.map((participant) => (
                                            <TableRow key={participant.participation_id}>
                                                <TableCell>
                                                    {participant.elderly_surname} {participant.elderly_name}
                                                </TableCell>
                                                <TableCell>{participant.n_room || '-'}</TableCell>
                                                <TableCell>
                                                    <Chip 
                                                        label={participant.attendance_status} 
                                                        size="small" 
                                                        color={participant.attendance_status === 'attended' ? 'success' : 'default'}
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    <IconButton
                                                        size="small"
                                                        color="error"
                                                        onClick={() => handleUnregisterParticipant(participant.elderly_id)}
                                                    >
                                                        <DeleteIcon fontSize="small" />
                                                    </IconButton>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>

                            <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
                                Aggiungi Partecipante
                            </Typography>
                            <TableContainer>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Nome</TableCell>
                                            <TableCell>Camera</TableCell>
                                            <TableCell>Azioni</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {elderly
                                            .filter(e => !selectedEvent.participants?.some(p => p.elderly_id === e.elderly_id))
                                            .map((elder) => (
                                                <TableRow key={elder.elderly_id}>
                                                    <TableCell>
                                                        {elder.surname} {elder.name}
                                                    </TableCell>
                                                    <TableCell>{elder.n_room || '-'}</TableCell>
                                                    <TableCell>
                                                        <Button
                                                            size="small"
                                                            variant="outlined"
                                                            onClick={() => handleRegisterParticipant(elder.elderly_id)}
                                                        >
                                                            Iscri vi
                                                        </Button>
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenParticipantsDialog(false)}>Chiudi</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default EventsPage;
